import React, {useCallback, useEffect, useRef, useState} from "react";
import {RouterPage} from "./RouterPage";
import {Box, Button, Card, Image, Loader, Text, Title} from "@mantine/core";
import {useNeurotuneStore} from "../../common/store/neurotune";
import {NIL as niluuid} from "uuid"
import {LoginPage} from "./LoginPage";
import {whoAmI} from "../../common/services/userService";
import {returnToPostLoginTargetIfNeeded} from "../../common/services/reauthRedirect";
import {invokeTauriCommand, isTauriAvailable} from "@/common/utils/tauri";
import loginClasses from "./LoginPage.module.css";

/** Delay between automatic bootstrap retries while waiting for the server. */
export const BOOTSTRAP_RETRY_DELAY_MS = 5_000

/** After this many failed attempts the panel switches to persistent-failure wording. */
export const BOOTSTRAP_PERSISTENT_FAILURE_ATTEMPTS = 5

/**
 * Full-screen status panel shown while the app bootstraps (whoAmI ->
 * companies -> subjects). Replaces the old bare LoadingOverlay, which
 * rendered as a blank page whenever the bootstrap retried or stranded.
 */
export const BootstrapStatusPanel: React.FC<{ attempt: number, onRetryNow: () => void }> = ({attempt, onRetryNow}) => {
    const persistent = attempt >= BOOTSTRAP_PERSISTENT_FAILURE_ATTEMPTS
    const message = attempt === 0
        ? "Connecting to Neurotune…"
        : persistent
            ? "Still unable to reach Neurotune. Check the Wi-Fi connection, then retry."
            : "Waiting for connection — retrying automatically."
    return (
        <Box style={{
            height: "100%",
            display: "grid",
            grid: "1fr / 1fr",
            justifyItems: "center",
            alignItems: "center",
        }}>
            <Card shadow="sm" w={400} p={0}>
                <Box className={loginClasses.root}>
                    <Image className={loginClasses.logo} src="/assets/images/BIOS_Logo_White_RGB.png"
                           alt="Neurotune Logo" width={100}/>
                    <Title c="var(--bios-light-text)" order={1} mb="md">Neurotune</Title>
                    <Loader color="var(--bios-light-text)" data-testid="bootstrap-loader"/>
                    <Text c="var(--bios-light-text)" ta="center" data-testid="bootstrap-message">{message}</Text>
                    {attempt > 0 && (
                        <Text c="var(--bios-light-text)" ta="center" size="sm"
                              data-testid="bootstrap-attempt">Attempt {attempt}</Text>
                    )}
                    <Button fullWidth variant="outline" bg={"var(--bios-brand-secondary-light)"}
                            onClick={onRetryNow} data-testid="bootstrap-retry-now">
                        Retry now
                    </Button>
                </Box>
            </Card>
        </Box>
    )
}

export const LoadingPage: React.FC = () => {
    const loadCompanies = useNeurotuneStore((state) => state.loadCompanies)
    const loadSubjects = useNeurotuneStore((state) => state.loadSubjects);
    const activeCompany = useNeurotuneStore((state) => state.activeCompany)
    const setActiveCompany = useNeurotuneStore((state) => state.setActiveCompany)
    const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
    const [loginRequired, setLoginRequired] = useState<boolean>(false)
    const [attempt, setAttempt] = useState<number>(0)
    // window.setTimeout returns a number in the DOM lib (Node's Timeout type
    // is never what the browser hands back).
    const retryTimer = useRef<number | null>(null)
    const unmounted = useRef(false)
    const appReadySignalled = useRef(false)

    // Auth expiry in redirect mode (NDC webview): any 401 sets this store
    // flag via the apiClient interceptor (reauthRedirect.reportAuthExpired).
    // The gate renders the LoginPage from it — in place, so the current deep
    // link survives re-login. A plain store read needs no listener
    // registration and re-renders via the same path as the Online indicator.
    const authExpired = useNeurotuneStore((state) => state.authExpired)
    const setAuthExpired = useNeurotuneStore((state) => state.setAuthExpired)
    useEffect(() => {
        if (authExpired) clearRetryTimer()
    }, [authExpired])

    const clearRetryTimer = () => {
        if (retryTimer.current !== null) {
            window.clearTimeout(retryTimer.current)
            retryTimer.current = null
        }
    }

    // Bootstrap: whoAmI -> companies -> active company. Any failure except
    // 401 (login required) schedules an automatic retry, but unlike the old
    // implementation the UI shows what is happening instead of a blank
    // overlay. A failed companies fetch also retries — the old code stranded
    // forever because loadCompanies swallowed the error and returned [].
    const bootstrap = useCallback(() => {
        clearRetryTimer()
        // check if we are logged in with whoAmI first, if not show the login page but don't allow
        // retries here or trigger login popup on failure as we are in the login page already
        // also don't show error notifications if this fails as it's expected behavior when not logged in
        whoAmI({retry: false, isAfterLoginRetry: true, silent: true}).then(() => {
            if (unmounted.current) return
            // After a redirect-mode login round-trip we land back on the app
            // root (the OAuth URLs are fixed registrations and cannot carry
            // a deep link). LoginPage stored where the user came from — go
            // back there now. No-op for in-place logins and browser mode.
            if (returnToPostLoginTargetIfNeeded()) {
                // Navigation is underway; stop the bootstrap chain.
                return new Promise<never>(() => {})
            }
            setLoginRequired(false)
            setAuthExpired(false)
            setAttempt(0)
            return loadCompanies()
        }).then((companies) => {
            if (unmounted.current) return
            if (companies && companies.length > 0) {
                setActiveCompany(companies[0])
            } else {
                // Successful response with zero companies is a terminal
                // state: stop retrying and let the render below show the
                // "not assigned to any companies" message.
                setLoadingComplete(true)
            }
        }).catch((error) => {
            if (unmounted.current) return
            if (error?.response && error.response.status === 401) {
                setLoginRequired(true)
                return
            }
            // Network failure, timeout, 5xx, or companies fetch failure:
            // surface it and retry automatically.
            setAttempt((a) => a + 1)
            retryTimer.current = window.setTimeout(bootstrap, BOOTSTRAP_RETRY_DELAY_MS)
        });
    }, [loadCompanies, setActiveCompany])

    useEffect(() => {
        unmounted.current = false
        bootstrap()
        return () => {
            unmounted.current = true
            clearRetryTimer()
        }
    }, [bootstrap]);

    // load the subjects when the active company changes
    useEffect(() => {
        if (activeCompany.id !== niluuid) {
            setLoadingComplete(false)
            loadSubjects(activeCompany.id).finally(() => {
                setLoadingComplete(true)
            })
        }
    }, [activeCompany]);

    // Signal the NDC loader webview (if any) that the app has reached a
    // stable, interactive state so it can swap itself out. "Stable" means
    // either the login page (login required) or a completed bootstrap —
    // crucially it must include the login-required case, otherwise an
    // unauthenticated NDC device sits behind the loader forever. The
    // connecting/waiting panel is deliberately NOT signalled: the loader's
    // own status messages cover that period better. Guarded: browser mode
    // and missing command are both no-ops. Fires once per page load.
    const appStable = loginRequired || authExpired || loadingComplete
    useEffect(() => {
        if (appStable && !appReadySignalled.current && isTauriAvailable()) {
            appReadySignalled.current = true
            invokeTauriCommand('app_ready').catch(() => {
                // Older NDC builds don't have this command — not an error.
            })
        }
    }, [appStable]);

    if (loginRequired || authExpired) { // if we need to login
        // onSuccess resumes bootstrap in place: no navigation, so the
        // current URL (deep link) survives the re-login.
        return <LoginPage onSuccess={bootstrap}/>
    } else if (!loadingComplete) { // bootstrapping (or retrying) — never render a blank page
        return <BootstrapStatusPanel attempt={attempt} onRetryNow={bootstrap}/>
    } else if (activeCompany.id !== niluuid) {
        // if the current active company has been properly set
        return <RouterPage/>
    } else {
        return ("You have not been assigned to any companies at this time. Please contact support@bios.ltd")
    }
}
